import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock3, HeartPulse, MessageCircle, PhoneCall, RefreshCw, ShieldAlert, Webhook } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { count, dateTime, LoadState, Metric, Pager, tableClass } from '@/components/ControlCenter';
import { controlCenterService, WhatsappHealthRow } from '@/services/control-center.service';
import { useAdminResource } from '@/hooks/useAdminResource';

const QUALITY: Record<string, { label: string; tone: string }> = {
  GREEN: { label: 'High', tone: 'bg-success-soft text-success' },
  YELLOW: { label: 'Medium', tone: 'bg-warning-soft text-warning' },
  RED: { label: 'Low', tone: 'bg-danger-soft text-danger' },
};

function WebhookCell({ row }: { row: WhatsappHealthRow }) {
  if (row.webhookFailed) {
    return <span className="flex items-center gap-1 text-danger" title={row.webhookSubscriptionError}><AlertTriangle size={12} />{row.webhookSubscriptionStatus === 'disconnected' ? 'Disconnected' : 'Needs repair'}</span>;
  }
  if (row.webhookSubscribedAt || row.webhookSubscriptionStatus === 'subscribed') {
    return <span className="flex items-center gap-1 text-success"><CheckCircle2 size={12} />Subscribed</span>;
  }
  return <span className="text-text-muted">{row.webhookSubscriptionStatus === 'pending' ? 'Pending' : 'Not verified'}</span>;
}

export default function WhatsappHealthPage() {
  const [page, setPage] = useState(1);
  const [attention, setAttention] = useState(false);
  const { data, loading, error, refresh } = useAdminResource(useCallback(
    () => controlCenterService.whatsapp({ page, ...(attention ? { attention: '1' as const } : {}) }), [page, attention]));
  const summary = data?.summary;

  return <>
    <Topbar title="WhatsApp health" breadcrumb="Platform / WhatsApp Health"
      right={<Button variant="ghost" size="sm" loading={loading} onClick={refresh} iconLeft={<RefreshCw size={14} />}>Refresh</Button>} />
    <div className="admin-page space-y-4">
      {summary && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Metric label="Numbers" value={count(summary.total)} detail="Connected across companies" icon={PhoneCall} />
          <Metric label="In use" value={count(summary.active)} detail="Active sending numbers" icon={MessageCircle} tone="success" />
          <Metric label="Quality at risk" value={count(summary.lowQuality)} detail="Medium or low Meta quality" icon={HeartPulse} tone={summary.lowQuality ? 'warning' : 'success'} />
          <Metric label="Webhook problems" value={count(summary.webhookProblems)} detail="Subscription failed or disconnected" icon={Webhook} tone={summary.webhookProblems ? 'danger' : 'success'} />
          <Metric label="Health errors" value={count(summary.healthErrors)} detail="Last provider check failed" icon={ShieldAlert} tone={summary.healthErrors ? 'danger' : 'success'} />
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs text-text-secondary">
          <input type="checkbox" checked={attention} onChange={e => { setAttention(e.target.checked); setPage(1); }} className="h-4 w-4 rounded border-border accent-[var(--color-primary)]" />
          Only numbers that need attention
        </label>
        <Link className="text-xs font-medium text-primary" to="/integrations?group=whatsapp">Connection settings →</Link>
      </div>
      <LoadState loading={loading} error={error} hasData={!!data} retry={refresh} />
      {data && <section className="admin-panel overflow-hidden">
        <div className="overflow-x-auto"><table className={tableClass}>
          <thead className="bg-surface-alt"><tr>
            <th>Number</th><th>Company</th><th>Status</th><th>Quality</th><th>Tier</th><th>Webhook</th><th>Last customer message</th><th>Last API check</th><th>Next step</th>
          </tr></thead>
          <tbody>{data.rows.map(row => {
            const quality = QUALITY[row.qualityRating ?? ''] ?? { label: 'Unknown', tone: 'bg-surface-alt text-text-muted' };
            const cooling = !!row.cooldownUntil && new Date(row.cooldownUntil).getTime() > Date.now();
            return <tr key={row.accountUid}>
              <td>
                <span className="block font-semibold">{row.verifiedName || row.businessName}</span>
                <span className="block text-[11px] text-text-muted">{row.displayPhoneNumber}</span>
                {row.wabaId && <span className="block font-mono text-[10px] text-text-muted">WABA {row.wabaId}</span>}
              </td>
              <td><Link to={`/companies/${row.companyUid}`} className="hover:text-primary">{row.companyName}</Link></td>
              <td>
                <span className={row.isActive ? 'text-success' : 'text-text-muted'}>{row.isActive ? 'In use' : 'Inactive'}</span>
                {row.isDefault && <span className="ml-1 rounded bg-primary-soft px-1.5 py-0.5 text-[10px] text-primary">Default</span>}
                <span className="block text-[11px] text-text-muted">{row.botMode === 'ai_chat' ? 'AI replies' : row.botMode === 'flow' ? 'Scripted flow' : 'Automation off'}</span>
              </td>
              <td><span className={`rounded-full px-2 py-1 text-[10px] font-medium ${quality.tone}`}>{quality.label}</span></td>
              <td>{row.messagingLimitTier ?? 'Not recorded'}</td>
              <td>
                <WebhookCell row={row} />
                <span className="block text-[11px] text-text-muted">{row.lastWebhookAt ? `event ${dateTime(row.lastWebhookAt)}` : 'No event recorded'}</span>
              </td>
              <td className="text-text-muted">{dateTime(row.lastInboundAt)}</td>
              <td>
                <span className="block text-text-muted">{dateTime(row.healthFetchedAt ?? row.healthAttemptedAt)}</span>
                {row.healthError && <span className="block max-w-[14rem] truncate text-[11px] text-danger" title={row.healthError}>{row.healthError}</span>}
              </td>
              <td>
                {row.reconnectWarning
                  ? <span className="flex max-w-[16rem] items-start gap-1 whitespace-normal text-warning"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{row.reconnectWarning}</span>
                  : <span className="flex items-center gap-1 text-success"><CheckCircle2 size={12} />No action</span>}
                {cooling && <span className="mt-1 flex items-center gap-1 text-[11px] text-warning"><Clock3 size={11} />Paused until {dateTime(row.cooldownUntil)}</span>}
              </td>
            </tr>;
          })}</tbody>
        </table></div>
        {!data.rows.length && <p className="p-8 text-center text-sm text-text-muted">{attention ? 'No numbers need attention.' : 'No WhatsApp numbers connected yet.'}</p>}
        <Pager {...data} onChange={setPage} loading={loading} />
      </section>}
      <p className="text-xs text-text-muted">Status comes from each number's last saved provider check and the webhook events this platform received; “Last customer message” and webhook times are recorded at most once a minute.</p>
    </div>
  </>;
}
